/**
 * Short URL Fetching Module
 *
 * Provides parallel fetching of platform-specific short URLs for social sharing.
 * Each platform needs a different short URL because the `source` tracking
 * parameter differs (e.g., source=app_share_whatsapp vs source=app_share_telegram).
 *
 * Caching is handled by React Query in usePrefetchShareUrls hook.
 */

import { getShortShareUrl, getShareUrl, type ShareUrlOptions } from '@utils/shareHelpers';
import { SocialPlatform, type ShareContext, ShareEntityType } from '@utils/shareConfig';
import { logger } from '@utils/logger';

// =============================================================================
// TYPES
// =============================================================================

export interface PrefetchResult {
  platform: SocialPlatform;
  url: string;
  success: boolean;
}

export type PlatformUrlsResult = PrefetchResult[];

// =============================================================================
// PLATFORMS
// =============================================================================

// All platforms that need pre-fetched URLs
const PLATFORMS_TO_PREFETCH: SocialPlatform[] = [
  SocialPlatform.WhatsApp,
  SocialPlatform.Telegram,
  SocialPlatform.LinkedIn,
  SocialPlatform.Facebook,
  SocialPlatform.Twitter,
  SocialPlatform.Email,
  SocialPlatform.Copy,
  SocialPlatform.Native,
];

// =============================================================================
// ENTITY ID EXTRACTION
// =============================================================================

/**
 * Extract entity ID from share context
 */
export function getEntityId(context: ShareContext): string {
  switch (context.entityType) {
    case ShareEntityType.Question:
      return context.questionId;
    case ShareEntityType.Event:
      return context.eventId;
    case ShareEntityType.Profile:
      return context.profileId;
    case ShareEntityType.Community:
      return context.communityId;
    case ShareEntityType.App:
      // App invites have no per-entity id - the app itself is the entity
      return 'app';
  }
}

/**
 * Build ShareUrlOptions for a specific platform from context
 */
function buildOptionsForPlatform(context: ShareContext, platform: SocialPlatform): ShareUrlOptions {
  const base: ShareUrlOptions = {
    sharerId: context.sharerId,
    platform,
  };

  switch (context.entityType) {
    case ShareEntityType.Event:
      return { ...base, eventId: context.eventId };
    case ShareEntityType.Question:
      return { ...base, questionId: context.questionId };
    case ShareEntityType.Profile:
      return { ...base, profileId: context.profileId };
    case ShareEntityType.Community:
    case ShareEntityType.App:
      return base;
  }
}

// =============================================================================
// FETCH
// =============================================================================

/**
 * Fetch all platform-specific short URLs in parallel
 *
 * Uses Promise.allSettled to ensure all fetches complete regardless of
 * individual failures. Failed fetches will fall back to long URLs.
 *
 * This function is called by React Query - caching is handled there.
 *
 * @param context - The share context containing path and entity info
 * @returns Promise resolving to array of results (URL per platform)
 */
export async function fetchAllPlatformUrls(context: ShareContext): Promise<PlatformUrlsResult> {
  const { path, sharerId } = context;
  const entityId = getEntityId(context);

  logger.debug('Fetching short URLs for all platforms', { path, sharerId, entityId });

  const fetchPromises = PLATFORMS_TO_PREFETCH.map(async (platform): Promise<PrefetchResult> => {
    const options = buildOptionsForPlatform(context, platform);

    try {
      const shortUrl = await getShortShareUrl(path, options);
      return { platform, url: shortUrl, success: true };
    } catch (error) {
      // On failure, use long URL as fallback
      const fallbackUrl = getShareUrl(path, options);
      // Use error level so it's visible in production for monitoring shortener service health
      // Omit fallbackUrl to avoid logging user identifiers (ref_user, entity IDs)
      logger.error(`[Share] URL shortening failed for ${platform}`, {
        error,
        path,
        platform,
      });
      return { platform, url: fallbackUrl, success: false };
    }
  });

  const results = await Promise.allSettled(fetchPromises);

  // Extract results (allSettled always returns fulfilled promises here
  // since our inner promises catch errors and return PrefetchResult)
  return results.map(result => {
    if (result.status === 'fulfilled') {
      return result.value;
    }
    // This shouldn't happen since we catch errors above, but handle defensively
    return {
      platform: SocialPlatform.Copy,
      url: '',
      success: false,
    };
  });
}
